/**
 * Client-side text cleanup.
 *
 * Replaces the conversion that used to live behind the Koa `/gitlang/convert/text`
 * endpoint (migrated to the drool repo). This is a dependency-free normaliser that
 * covers the common cases the old `grammarify`-backed endpoint handled: stray
 * whitespace, doubled spaces, space before punctuation, and runaway blank lines.
 */

/** Punctuation that should hug the word before it (no leading space). */
const SPACE_BEFORE_PUNCTUATION = / +([!,.:;?])/g;

/** Two or more spaces/tabs collapse to a single space. */
const REPEATED_INLINE_WHITESPACE = /[^\S\n]{2,}/g;

/** Trailing spaces/tabs at the end of any line. */
const TRAILING_LINE_WHITESPACE = /[^\S\n]+$/gm;

/** Three or more newlines collapse to a paragraph break (two newlines). */
const EXCESS_BLANK_LINES = /\n{3,}/g;

/**
 * Normalise free-form text:
 * - trims leading/trailing whitespace
 * - collapses repeated inline whitespace to single spaces
 * - removes spaces before sentence punctuation
 * - strips trailing whitespace from each line
 * - collapses 3+ blank lines down to a single paragraph break
 *
 * Returns an empty string for empty/whitespace-only input.
 */
export const cleanText = (input: string): string =>
  input
    .replaceAll(REPEATED_INLINE_WHITESPACE, ' ')
    .replaceAll(SPACE_BEFORE_PUNCTUATION, '$1')
    .replaceAll(TRAILING_LINE_WHITESPACE, '')
    .replaceAll(EXCESS_BLANK_LINES, '\n\n')
    .trim();
